import axios from "axios";
import { base_url } from "../../constants";

/**
 * Question Bank API layer.
 *
 * Follows the convention used across this panel: every call is a POST of a
 * JSON string to a .php endpoint, carrying admin_id + access_token in the
 * body. The global axios interceptor (src/helpers/api_helper.js) already
 * unwraps response.data, so `res` here is the payload itself.
 */

const ROOT = `${base_url}/admin/question_bank`;

export const ENDPOINTS = {
  // Banks
  banksList: `${ROOT}/select_question_banks.php`,
  bankGet: `${ROOT}/select_question_bank.php`,
  bankCreate: `${ROOT}/add_question_bank.php`,
  bankUpdate: `${ROOT}/edit_question_bank.php`,
  bankDuplicate: `${ROOT}/duplicate_question_bank.php`,
  bankDelete: `${ROOT}/delete_question_bank.php`,
  bankStatus: `${ROOT}/set_question_bank_status.php`,

  // Units
  unitsList: `${ROOT}/select_bank_units.php`,
  unitCreate: `${ROOT}/add_bank_unit.php`,
  unitUpdate: `${ROOT}/edit_bank_unit.php`,
  unitDuplicate: `${ROOT}/duplicate_bank_unit.php`,
  unitMove: `${ROOT}/move_bank_unit.php`,
  unitReorder: `${ROOT}/arrange_bank_units.php`,
  unitDelete: `${ROOT}/delete_bank_unit.php`,

  // Lectures
  lecturesList: `${ROOT}/select_unit_lectures.php`,
  lectureGet: `${ROOT}/select_unit_lecture.php`,
  lectureCreate: `${ROOT}/add_unit_lecture.php`,
  lectureUpdate: `${ROOT}/edit_unit_lecture.php`,
  lectureDuplicate: `${ROOT}/duplicate_unit_lecture.php`,
  lectureMove: `${ROOT}/move_unit_lecture.php`,
  lectureReorder: `${ROOT}/arrange_unit_lectures.php`,
  lectureDelete: `${ROOT}/delete_unit_lecture.php`,

  // Questions
  questionsList: `${ROOT}/select_lecture_questions.php`,
  questionCreate: `${ROOT}/add_lecture_question.php`,
  questionBulkCreate: `${ROOT}/add_lecture_questions_bulk.php`,
  questionImport: `${ROOT}/import_lecture_questions.php`,
  questionUpdate: `${ROOT}/edit_lecture_question.php`,
  questionDelete: `${ROOT}/delete_lecture_question.php`,
  questionReorder: `${ROOT}/arrange_lecture_questions.php`,

  // Course unit -> bank unit deep link
  resolveCourseUnit: `${ROOT}/resolve_course_unit.php`,
  unitQuestions: `${ROOT}/select_unit_questions.php`,

  // Course associations
  assignmentsList: `${ROOT}/select_course_bank_assignments.php`,
  assignmentCreate: `${ROOT}/assign_bank_to_course.php`,
  assignmentUpdate: `${ROOT}/edit_course_bank_assignment.php`,
  assignmentDelete: `${ROOT}/remove_course_bank_assignment.php`,

  // Reused from the existing course module
  coursesList: `${base_url}/admin/courses/select_courses.php`,
};

export const getAdminData = () => {
  try {
    const raw = localStorage.getItem("elmatary_admin");
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

export class SessionExpiredError extends Error {
  constructor() {
    super("Session Expired");
    this.name = "SessionExpiredError";
  }
}

export class ApiError extends Error {
  constructor(message) {
    super(message || "An error occurred");
    this.name = "ApiError";
  }
}

/**
 * Core request helper.
 * Resolves with the payload on success, throws otherwise.
 */
export const qbRequest = async (url, payload = {}) => {
  const admin = getAdminData();
  const body = {
    ...payload,
    admin_id: admin?.admin_id,
    access_token: admin?.access_token,
  };

  const res = await axios.post(url, JSON.stringify(body));

  if (res?.message === "Session Expired") {
    localStorage.removeItem("elmatary_admin");
    throw new SessionExpiredError();
  }

  if (res?.status === "success") {
    return res.message;
  }

  // Some legacy endpoints answer with a bare array.
  if (Array.isArray(res)) return res;

  throw new ApiError(
    typeof res?.message === "string" ? res.message : "An error occurred"
  );
};

/** Normalises a list payload that may be an array or an object with data + pagination. */
export const asList = (payload) => {
  if (Array.isArray(payload)) return { rows: payload, pagination: null };
  if (Array.isArray(payload?.data)) {
    return { rows: payload.data, pagination: payload.pagination || null };
  }
  return { rows: [], pagination: null };
};

/* ------------------------------------------------------------------ */
/* Record normalisation                                                */
/* ------------------------------------------------------------------ */
/**
 * The backend names these columns `bank_id` / `bank_name` (and in some
 * endpoints reuses `course_id` / `course_name`), while this module works with
 * `question_bank_id` / `name` throughout. Every read goes through the
 * normalisers below and every write sends both spellings, so the UI keeps
 * working whichever naming an endpoint uses.
 */

const pick = (raw, keys, fallback = undefined) => {
  for (const key of keys) {
    const value = raw?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const toCount = (raw, keys) => Number(pick(raw, keys, 0)) || 0;

/** Absent status means active - never show a record as Inactive by accident. */
const normStatus = (value) => {
  if (value === undefined || value === null || value === "") return "active";
  const text = String(value).toLowerCase();
  if (["inactive", "0", "false", "no", "hidden"].includes(text)) return "inactive";
  return "active";
};

const normBoolean = (value) =>
  value === true || ["1", "true", "yes"].includes(String(value).toLowerCase());

export const normBank = (raw = {}) => ({
  ...raw,
  question_bank_id: pick(raw, ["question_bank_id", "bank_id", "id", "course_id"], ""),
  name: pick(raw, ["name", "bank_name", "title", "course_name"], ""),
  description: pick(raw, ["description", "bank_description", "details"], ""),
  status: normStatus(pick(raw, ["status", "bank_status", "active"])),
  units_count: toCount(raw, ["units_count", "unit_count", "units", "total_units"]),
  lectures_count: toCount(raw, [
    "lectures_count",
    "lecture_count",
    "lectures",
    "total_lectures",
  ]),
  questions_count: toCount(raw, [
    "questions_count",
    "question_count",
    "questions",
    "total_questions",
  ]),
  courses_count: toCount(raw, ["courses_count", "course_count", "total_courses"]),
  created_at: pick(raw, ["created_at", "create_date", "date"], ""),
});

export const normUnit = (raw = {}) => ({
  ...raw,
  unit_id: pick(raw, ["unit_id", "id"], ""),
  question_bank_id: pick(raw, ["question_bank_id", "bank_id"], ""),
  name: pick(raw, ["name", "unit_name", "title"], ""),
  description: pick(raw, ["description", "unit_description", "details"], ""),
  display_order: Number(pick(raw, ["display_order", "order_no", "sort_order"], 0)) || 0,
  status: normStatus(pick(raw, ["status", "unit_status", "active"])),
  lectures_count: toCount(raw, [
    "lectures_count",
    "lecture_count",
    "lectures",
    "total_lectures",
  ]),
  questions_count: toCount(raw, [
    "questions_count",
    "question_count",
    "questions",
    "total_questions",
  ]),
  courses_count: toCount(raw, ["courses_count", "course_count"]),
});

export const normLecture = (raw = {}) => ({
  ...raw,
  lecture_id: pick(raw, ["lecture_id", "id"], ""),
  unit_id: pick(raw, ["unit_id"], ""),
  name: pick(raw, ["name", "lecture_name", "title"], ""),
  description: pick(raw, ["description", "lecture_description", "details"], ""),
  display_order: Number(pick(raw, ["display_order", "order_no", "sort_order"], 0)) || 0,
  status: normStatus(pick(raw, ["status", "lecture_status", "active"])),
  questions_count: toCount(raw, [
    "questions_count",
    "question_count",
    "questions",
    "total_questions",
  ]),
});

export const normOption = (raw = {}) => ({
  ...raw,
  option_id: pick(raw, ["option_id", "id"], ""),
  option_text: pick(raw, ["option_text", "answer", "text", "option"], ""),
  is_correct: normBoolean(pick(raw, ["is_correct", "correct", "checked"], false)),
  display_order: Number(pick(raw, ["display_order", "order_no"], 0)) || 0,
});

export const normQuestion = (raw = {}) => {
  const options = pick(raw, ["options", "answers", "question_options"], []);
  return {
    ...raw,
    question_id: pick(raw, ["question_id", "id"], ""),
    lecture_id: pick(raw, ["lecture_id"], ""),
    question_text: pick(raw, ["question_text", "question", "text", "title"], ""),
    question_type: pick(raw, ["question_type", "type"], "mcq_single"),
    explanation: pick(raw, ["explanation", "explain", "answer_exp"], ""),
    correct_answer: pick(raw, ["correct_answer", "model_answer"], ""),
    difficulty_level: pick(raw, ["difficulty_level", "difficulty"], "medium"),
    marks: Number(pick(raw, ["marks", "mark", "score", "degree"], 1)) || 1,
    display_order: Number(pick(raw, ["display_order", "order_no"], 0)) || 0,
    status: normStatus(pick(raw, ["status", "active"])),
    options: Array.isArray(options) ? options.map(normOption) : [],
  };
};

export const normAssignment = (raw = {}) => {
  const units = pick(raw, ["units", "unit_ids", "selected_units"], []);
  return {
    ...raw,
    assignment_id: pick(raw, ["assignment_id", "id"], ""),
    course_id: pick(raw, ["course_id"], ""),
    question_bank_id: pick(raw, ["question_bank_id", "bank_id"], ""),
    assignment_mode:
      String(pick(raw, ["assignment_mode", "mode"], "FULL_BANK")).toUpperCase() ===
      "SELECTED_UNITS"
        ? "SELECTED_UNITS"
        : "FULL_BANK",
    units: Array.isArray(units)
      ? units.map((u) =>
          typeof u === "object"
            ? { unit_id: pick(u, ["unit_id", "id"], ""), name: pick(u, ["name", "unit_name"], "") }
            : { unit_id: u, name: "" }
        )
      : [],
    questions_count: toCount(raw, ["questions_count", "question_count", "total_questions"]),
  };
};

/** Applies a normaliser to a list payload, preserving the array shape. */
const normaliseList = (payload, fn) => asList(payload).rows.map(fn);

/** Applies a normaliser to a single-record payload that may arrive wrapped. */
const normaliseOne = (payload, fn) => {
  if (Array.isArray(payload)) return payload.length ? fn(payload[0]) : null;
  if (payload && typeof payload === "object") return fn(payload);
  return null;
};

/**
 * Adds the backend's column spellings alongside this module's own, so writes
 * succeed whichever names the endpoint reads.
 */
const withBankAliases = (data) => ({
  ...data,
  ...(data.question_bank_id !== undefined
    ? { bank_id: data.question_bank_id }
    : {}),
  ...(data.name !== undefined ? { bank_name: data.name } : {}),
});

const withUnitAliases = (data) => ({
  ...data,
  ...(data.question_bank_id !== undefined ? { bank_id: data.question_bank_id } : {}),
  ...(data.name !== undefined ? { unit_name: data.name } : {}),
  ...(data.display_order !== undefined ? { order_no: data.display_order } : {}),
});

const withLectureAliases = (data) => ({
  ...data,
  ...(data.name !== undefined ? { lecture_name: data.name } : {}),
  ...(data.display_order !== undefined ? { order_no: data.display_order } : {}),
});

const withQuestionAliases = (data) => ({
  ...data,
  ...(data.question_text !== undefined ? { question: data.question_text } : {}),
  ...(data.explanation !== undefined ? { explain: data.explanation } : {}),
  ...(data.display_order !== undefined ? { order_no: data.display_order } : {}),
});

/* ------------------------------------------------------------------ */
/* Banks                                                               */
/* ------------------------------------------------------------------ */
export const listBanks = async (params) =>
  normaliseList(await qbRequest(ENDPOINTS.banksList, params), normBank);
export const getBank = async (question_bank_id) =>
  normaliseOne(
    await qbRequest(ENDPOINTS.bankGet, withBankAliases({ question_bank_id })),
    normBank
  );
export const createBank = (data) =>
  qbRequest(ENDPOINTS.bankCreate, withBankAliases(data));
export const updateBank = (data) =>
  qbRequest(ENDPOINTS.bankUpdate, withBankAliases(data));
export const duplicateBank = (question_bank_id, name) =>
  qbRequest(ENDPOINTS.bankDuplicate, withBankAliases({ question_bank_id, name }));
export const deleteBank = (question_bank_id) =>
  qbRequest(ENDPOINTS.bankDelete, withBankAliases({ question_bank_id }));
export const setBankStatus = (question_bank_id, status) =>
  qbRequest(ENDPOINTS.bankStatus, withBankAliases({ question_bank_id, status }));

/* ------------------------------------------------------------------ */
/* Units                                                               */
/* ------------------------------------------------------------------ */
export const listUnits = async (question_bank_id) =>
  normaliseList(
    await qbRequest(ENDPOINTS.unitsList, withBankAliases({ question_bank_id })),
    normUnit
  );
export const createUnit = (data) =>
  qbRequest(ENDPOINTS.unitCreate, withUnitAliases(data));
export const updateUnit = (data) =>
  qbRequest(ENDPOINTS.unitUpdate, withUnitAliases(data));
export const duplicateUnit = (unit_id, name) =>
  qbRequest(ENDPOINTS.unitDuplicate, withUnitAliases({ unit_id, name }));
export const moveUnit = (unit_id, question_bank_id) =>
  qbRequest(ENDPOINTS.unitMove, withBankAliases({ unit_id, question_bank_id }));
export const reorderUnits = (question_bank_id, ordered_unit_ids) =>
  qbRequest(ENDPOINTS.unitReorder, { question_bank_id, ordered_unit_ids });
export const deleteUnit = (unit_id) =>
  qbRequest(ENDPOINTS.unitDelete, { unit_id });

/* ------------------------------------------------------------------ */
/* Lectures                                                            */
/* ------------------------------------------------------------------ */
export const listLectures = async (unit_id) =>
  normaliseList(await qbRequest(ENDPOINTS.lecturesList, { unit_id }), normLecture);
export const getLecture = async (lecture_id) =>
  normaliseOne(await qbRequest(ENDPOINTS.lectureGet, { lecture_id }), normLecture);
export const createLecture = (data) =>
  qbRequest(ENDPOINTS.lectureCreate, withLectureAliases(data));
export const updateLecture = (data) =>
  qbRequest(ENDPOINTS.lectureUpdate, withLectureAliases(data));
export const duplicateLecture = (lecture_id, name) =>
  qbRequest(ENDPOINTS.lectureDuplicate, withLectureAliases({ lecture_id, name }));
export const moveLecture = (lecture_id, unit_id) =>
  qbRequest(ENDPOINTS.lectureMove, { lecture_id, unit_id });
export const reorderLectures = (unit_id, ordered_lecture_ids) =>
  qbRequest(ENDPOINTS.lectureReorder, { unit_id, ordered_lecture_ids });
export const deleteLecture = (lecture_id) =>
  qbRequest(ENDPOINTS.lectureDelete, { lecture_id });

/* ------------------------------------------------------------------ */
/* Questions                                                           */
/* ------------------------------------------------------------------ */
export const listQuestions = async (params) =>
  normaliseList(await qbRequest(ENDPOINTS.questionsList, params), normQuestion);
export const createQuestion = (data) =>
  qbRequest(ENDPOINTS.questionCreate, withQuestionAliases(data));
/**
 * Bulk create. The whole array is sent in one request so the backend can wrap
 * it in a single transaction - a partial save must never happen.
 */
export const createQuestionsBulk = (lecture_id, questions) =>
  qbRequest(ENDPOINTS.questionBulkCreate, { lecture_id, questions });
export const importQuestions = (lecture_id, questions) =>
  qbRequest(ENDPOINTS.questionImport, { lecture_id, questions });
export const updateQuestion = (data) =>
  qbRequest(ENDPOINTS.questionUpdate, withQuestionAliases(data));
export const deleteQuestion = (question_id) =>
  qbRequest(ENDPOINTS.questionDelete, { question_id });
export const reorderQuestions = (lecture_id, ordered_question_ids) =>
  qbRequest(ENDPOINTS.questionReorder, { lecture_id, ordered_question_ids });

/* ------------------------------------------------------------------ */
/* Course associations                                                 */
/* ------------------------------------------------------------------ */
export const listAssignments = async (params) =>
  normaliseList(await qbRequest(ENDPOINTS.assignmentsList, params), normAssignment);
export const createAssignment = (data) =>
  qbRequest(ENDPOINTS.assignmentCreate, withBankAliases(data));
export const updateAssignment = (data) =>
  qbRequest(ENDPOINTS.assignmentUpdate, withBankAliases(data));
export const deleteAssignment = (assignment_id) =>
  qbRequest(ENDPOINTS.assignmentDelete, { assignment_id });

export const listCourses = () => qbRequest(ENDPOINTS.coursesList, {});

/**
 * Resolves a course unit (mirrored from a bank) to its bank, bank unit and
 * lectures, so the course's "Questions" button can jump into the bank.
 */
export const resolveCourseUnit = (params) =>
  qbRequest(ENDPOINTS.resolveCourseUnit, params);

/** Every question in a bank unit (all lectures), plus the unit and lectures. */
export const listUnitQuestions = (params) =>
  qbRequest(ENDPOINTS.unitQuestions, params);

